import React, { useEffect, useMemo, useState } from "react";
import { POSTS } from "./data";
import { Link } from "react-router-dom";

const PRIMARY = "#1A4564";
const ACCENT = "#BEE363";

/**
 * ✅ UI categories
 */
const CATEGORIES = ["All", "Webdesign", "Reputationsmanagement", "Ai-Automation"];

/**
 * ✅ From your data:
 * Webdesign-related tags: "Webdesign", "Webentwicklung"
 * Ai-Automation-related tags: "CRM Automatisierung", "Automatisierung"
 * Reputation-related tags: "Local Listing", "Bewertungen"
 *
 * So filters must match MULTIPLE tags, not just one exact tag.
 */
const CATEGORY_MATCHERS = {
  Webdesign: (tag) => ["Webdesign", "Webentwicklung"].includes(tag),
  "Ai-Automation": (tag) => ["CRM Automatisierung", "Automatisierung"].includes(tag),
  Reputationsmanagement: (tag) => ["Local Listing", "Bewertungen"].includes(tag),
};

// ✅ button colors (one color per UI category)
const CATEGORY_STYLES = {
  Webdesign: { bg: "#7C3AED", text: "#FFFFFF" },
  "Ai-Automation": { bg: "#2563EB", text: "#FFFFFF" },
  Reputationsmanagement: { bg: "#F59E0B", text: "#1F2937" },
};

const PAGE_SIZE = 8;

function BottomMeta({ avatar, name, date }) {
  return (
    <div className="mt-4 flex items-center justify-between text-xs 2xl:text-sm text-slate-500">
      <div className="flex items-center gap-2">
        <img src={avatar} alt={name} className="h-7 w-7 rounded-full object-cover" />
        <span className="font-medium">{name}</span>
      </div>
      <span className="font-medium">{date}</span>
    </div>
  );
}

// ✅ Convert raw data tag into your 3 UI buckets (for display chip)
function prettyTagFromRaw(rawTag) {
  const t = (rawTag || "").trim();

  if (CATEGORY_MATCHERS.Webdesign(t)) return "Webdesign";
  if (CATEGORY_MATCHERS["Ai-Automation"](t)) return "Ai-Automation";
  if (CATEGORY_MATCHERS.Reputationsmanagement(t)) return "Reputationsmanagement";

  // fallback
  return t || "Webdesign";
}

function BottomCard({ post }) {
  const uiTag = prettyTagFromRaw(post.tag);
  const chipStyle = CATEGORY_STYLES[uiTag] || { bg: ACCENT, text: PRIMARY };

  return (
    <Link to={`/ratgeber/${post.slug}`} className="group block">
      <article className="cursor-pointer">
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_12px_30px_rgba(2,10,20,0.08)]">
          <div className="relative h-[175px]">
            <img src={post.image} alt={post.title} className="h-full w-full object-cover" />
          </div>

          <div className="relative p-4">
            <div className="-mt-7 mb-3">
              <span
                className="inline-flex items-center rounded-full px-3 py-1 text-xs 2xl:text-sm font-semibold shadow-sm"
                style={{ backgroundColor: chipStyle.bg, color: chipStyle.text }}
              >
                {uiTag}
              </span>
            </div>

            <h3
              className="text-base font-extrabold leading-snug transition-opacity duration-200 group-hover:opacity-70"
              style={{ color: PRIMARY }}
            >
              {post.title}
            </h3>

            <p className="mt-2 text-sm 2xl:text-lg text-slate-500 line-clamp-2 transition-opacity duration-200 group-hover:opacity-60">
              {post.excerpt}
            </p>

            <BottomMeta avatar={post.authorAvatar} name={post.author} date={post.date} />
          </div>
        </div>
      </article>
    </Link>
  );
}

// ✅ small helper: shows nice pagination like: Prev 1 2 3 ... 8 Next
function getPageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const pages = new Set([1, total, current, current - 1, current + 1, current - 2, current + 2]);
  const filtered = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);

  const result = [];
  for (let i = 0; i < filtered.length; i++) {
    const p = filtered[i];
    const prev = filtered[i - 1];
    if (i > 0 && p - prev > 1) result.push("...");
    result.push(p);
  }
  return result;
}

function Pagination({ page, totalPages, onChange }) {
  const pageItems = useMemo(() => getPageNumbers(page, totalPages), [page, totalPages]);
  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div className="mt-10 flex flex-wrap items-center justify-center gap-2">
      <button
        onClick={() => canPrev && onChange(page - 1)}
        disabled={!canPrev}
        className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm 2xl:text-lg font-semibold text-slate-700 transition disabled:cursor-not-allowed disabled:opacity-50"
      >
        Previous
      </button>

      {pageItems.map((item, idx) => {
        if (item === "...") {
          return (
            <span key={`dots-${idx}`} className="px-2 text-slate-400">
              ...
            </span>
          );
        }

        const p = item;
        const active = p === page;

        return (
          <button
            key={p}
            onClick={() => onChange(p)}
            className="h-10 min-w-10 rounded-full border px-3 text-sm 2xl:text-lg font-semibold transition"
            style={{
              borderColor: active ? PRIMARY : "#E2E8F0",
              backgroundColor: active ? PRIMARY : "white",
              color: active ? "white" : "#334155",
            }}
          >
            {p}
          </button>
        );
      })}

      <button
        onClick={() => canNext && onChange(page + 1)}
        disabled={!canNext}
        className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm 2xl:text-lg font-semibold text-slate-700 transition disabled:cursor-not-allowed disabled:opacity-50"
      >
        Next
      </button>
    </div>
  );
}

function CategoryFilters({ active, onChange }) {
  return (
    <div className="mb-8 flex flex-wrap items-center gap-3">
      {CATEGORIES.map((cat) => {
        const isActive = cat === active;
        const style = CATEGORY_STYLES[cat] || { bg: PRIMARY, text: "#FFFFFF" };

        return (
          <button
            key={cat}
            onClick={() => onChange(cat)}
            className="rounded-full border px-4 py-2 text-sm 2xl:text-base font-semibold transition"
            style={{
              borderColor: isActive ? style.bg : "#E2E8F0",
              backgroundColor: isActive ? style.bg : "white",
              color: isActive ? style.text : "#334155",
            }}
          >
            {cat}
          </button>
        );
      })}
    </div>
  );
}

export default function BottomRowCards() {
  const [page, setPage] = useState(1);
  const [activeCategory, setActiveCategory] = useState("All");

  // ✅ filter posts by UI category -> matches multiple raw tags
  const filteredPosts = useMemo(() => {
    if (activeCategory === "All") return POSTS;

    const matcher = CATEGORY_MATCHERS[activeCategory];
    if (!matcher) return POSTS;

    return POSTS.filter((p) => matcher((p.tag || "").trim()));
  }, [activeCategory]);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(filteredPosts.length / PAGE_SIZE)),
    [filteredPosts.length]
  );

  useEffect(() => {
    setPage(1);
  }, [activeCategory]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pagePosts = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filteredPosts.slice(start, start + PAGE_SIZE);
  }, [page, filteredPosts]);

  return (
    <section className="w-full bg-white mt-8">
      <div className="mx-auto w-[90%] 2xl:w-[70%] py-20">
        <div className="md:flex md:justify-between ">
           <h2 className="mb-4 text-2xl font-extrabold tracking-tight text-[#1A4564]">
          All Articles
        </h2>

        <CategoryFilters active={activeCategory} onChange={setActiveCategory} />

        </div>
       

        {pagePosts.length === 0 ? (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-8 text-slate-600">
            No articles found in <span className="font-semibold">{activeCategory}</span>.
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {pagePosts.map((post, idx) => (
              <BottomCard key={`${post.slug}-${idx}`} post={post} />
            ))}
          </div>
        )}

        {filteredPosts.length > PAGE_SIZE && (
          <Pagination page={page} totalPages={totalPages} onChange={setPage} />
        )}
      </div>
    </section>
  );
}
